import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {Title} from '@angular/platform-browser';
import memo from 'memo-decorator';
import {BehaviorSubject, combineLatest, EMPTY, zip} from 'rxjs';
import {catchError, map, shareReplay, take, tap} from 'rxjs/operators';
import {AnalyticsServiceNameModel, TelemetryService} from 'telemetry-library';

import {LpAccreditationService} from 'src/app/services/lp/lp-accreditation.service';
import {UserService} from 'src/app/services/shared/user.service';
import {isNullOrUndefined, TerraUtils} from 'src/app/shared/TerraUtils';
import {AppSettingsService} from '../../services/app-settings.service';
import {LpInvestingEntityDataService} from '../../services/lp/lp-investing-entity-data.service';
import {RoutingService} from '../../services/routing.service';
import {ImpersonationService} from '../../services/shared/impersonation.service';
import {LpAssetsService} from '../assets/lp-assets.service';
import {FundsService} from '../funds/lp-funds.service';
import {LpUiService} from '../shared/lp-ui.service';
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import {LpHoldingDataService} from 'src/app/services/lp/lp-holding-data.service';

interface LpHoldingsForm {
  searchTerm: string;
}

@Component({
  selector: 'terra-holdings',
  templateUrl: './lp-holdings.component.html',
  styleUrls: [
    './lp-holdings.component.scss',
    '../styles/holding/holding-page-shared.scss',
  ],
  providers: [FundsService, LpAssetsService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LpHoldingsComponent implements OnInit {
  @Output() updateFilter = new EventEmitter();

  get isMobile(): boolean {
    return TerraUtils.isMobileDevice();
  }

  hasPendingInvestments = false;
  hasOpportunitiesInvestments = false;

  pageForm: UntypedFormGroup;
  isLoading$ = new BehaviorSubject<boolean>(true);
  numberOfActiveHoldings$ = new BehaviorSubject<number>(0);
  numberOfHoldings$ = new BehaviorSubject<number>(0);
  isGeneralServerError$ = new BehaviorSubject<boolean>(false);

  filteredActiveInvestments: {assets: any[]; funds: any[]} = {assets: [], funds: []};
  private activeInvestments: {assets: any[]; funds: any[]};
  filteredPendingInvestments: {assets: any[]; funds: any[]} = {assets: [], funds: []};
  private pendingInvestments: {assets: any[]; funds: any[]};
  filteredOpportunitiesInvestments: {assets: any[]; funds: any[]} = {assets: [], funds: []};
  private opportunitiesInvestments: {assets: any[]; funds: any[]};

  activeInvestments$ = this.lpHoldingDataService.getProtfolioActiveInvestments().pipe(
    map((investments) => {
      const assets = [];
      const funds = [];
      investments.forEach(investment => investment.discriminator === "Asset" ? assets.push(investment) : funds.push(investment));
      this.filteredActiveInvestments = {assets, funds};
      this.activeInvestments = {assets, funds};
      return {assets, funds};
    })
  );

  investingEntities$ = this.lpInvestingEntityDataService.getProtfolioInvestingEntitiesByCurrentClient().pipe(
    map(allEntities => allEntities.filter(entity => entity.type !== 'OwnerSharing')),
    tap(allEntities => this.filter([], allEntities))
  );

  pendingInvestments$ = this.lpHoldingDataService.getProtfolioPendingInvestments().pipe(
    map((investments) => {
      const assets = [];
      const funds = [];
      investments.forEach(investment => investment.discriminator === "Asset" ? assets.push(investment) : funds.push(investment));
      this.filteredPendingInvestments = {assets, funds};
      this.pendingInvestments = {assets, funds};
      this.hasPendingInvestments = !!(this.filteredPendingInvestments.assets?.length || this.filteredPendingInvestments.funds?.length);
      return {assets, funds};
    })
  );

  investmentsOpportunities$ = this.lpHoldingDataService.getProtfolioOpportunities().pipe(
    map((opportunities) => {
      const assets = [];
      const funds = [];
      opportunities.forEach(investment => investment.discriminator === "Asset" ? assets.push(investment) : funds.push(investment));
      this.filteredOpportunitiesInvestments = {assets, funds};
      this.opportunitiesInvestments = {assets, funds};
      this.hasOpportunitiesInvestments = !!(this.filteredOpportunitiesInvestments.assets?.length || this.filteredOpportunitiesInvestments.funds?.length);
      return {assets, funds};
    })
  );

  filteredHoldingsAmounts$ = combineLatest([this.activeInvestments$, this.pendingInvestments$, this.investmentsOpportunities$, this.updateFilter]).pipe(
    map(([_active, _pending, _opportunities, _]) => {
      this.isLoading$.next(false);
      const numberOfAssets = this.filteredActiveInvestments.assets?.length + this.filteredPendingInvestments.assets?.length;
      const numberOfFunds = this.filteredActiveInvestments.funds?.length + this.filteredPendingInvestments.funds?.length;
      const numberOfOpportunities = this.filteredOpportunitiesInvestments.assets?.length + this.filteredOpportunitiesInvestments.funds?.length;
      const numberOfActiveHoldings = this.filteredActiveInvestments.assets?.length + this.filteredActiveInvestments.funds?.length;

      this.numberOfActiveHoldings$.next(numberOfActiveHoldings);
      this.numberOfHoldings$.next(numberOfAssets + numberOfFunds + numberOfOpportunities);
      return {assets: numberOfAssets, funds: numberOfFunds, opportunities: numberOfOpportunities};
    }));

  countTitle$ = this.filteredHoldingsAmounts$.pipe(
    map(amounts => this.getInvestmentsCountText(amounts.assets, amounts.funds, amounts.opportunities)),
    shareReplay(1)
  );

  get pageFormValue(): LpHoldingsForm {
    if (!this.pageForm) {
      return null;
    }
    return this.pageForm.value as LpHoldingsForm;
  }

  constructor(
    private titleService: Title,
    private fb: UntypedFormBuilder,
    private appSetting: AppSettingsService,
    private lpUiService: LpUiService,
    private telemetryService: TelemetryService,
    private fundService: FundsService,
    private assetService: LpAssetsService,
    public routingService: RoutingService,
    private impersonationService: ImpersonationService,
    private userService: UserService,
    private lpAccreditationService: LpAccreditationService,
    private lpInvestingEntityDataService: LpInvestingEntityDataService,
    private lpHoldingDataService: LpHoldingDataService,
    private readonly _bottomSheet: MatBottomSheet
  ) {
    this.lpUiService.showMainNav();
  }

  ngOnInit() {
    this.titleService.setTitle(
      'My portfolio' + TerraUtils.consts.LpPageTitleSuffix
    );
    this.generateForm();
    this.sendTelemetryEvent();
  }

  filter(investingEntities, allItems) {
    // update filter
    const ids = (investingEntities.length ? investingEntities : allItems).map((item) => item.id);
    if (!ids.length) {
      this.filteredActiveInvestments = {
        assets: this.activeInvestments?.assets,
        funds: this.activeInvestments?.funds,
      };
      this.filteredPendingInvestments = {
        assets: this.pendingInvestments?.assets,
        funds: this.pendingInvestments?.funds,
      };
      this.filteredOpportunitiesInvestments = {
        assets: this.opportunitiesInvestments?.assets,
        funds: this.opportunitiesInvestments?.funds,
      };
    } else {
      this.filteredActiveInvestments = {
        assets:
          this.activeInvestments?.assets?.filter((asset) =>
            asset.investingEntitiesIds.split(',').map(Number).some((id) => ids.includes(id))
          ) || [],
        funds:
          this.activeInvestments?.funds?.filter((fund) =>
            fund.investingEntitiesIds.split(',').map(Number).some((id) => ids.includes(id))
          ) || [],
      };
      this.filteredPendingInvestments = {
        assets:
          this.pendingInvestments?.assets?.filter((asset) =>
            asset.investingEntitiesIds.split(',').map(Number).some((id) => ids.includes(id))
          ) || [],
        funds:
          this.pendingInvestments?.funds?.filter((fund) =>
            fund.investingEntitiesIds.split(',').map(Number).some((id) => ids.includes(id))
          ) || [],
      };
      this.filteredOpportunitiesInvestments = {
        assets:
          this.opportunitiesInvestments?.assets?.filter((asset) =>
            asset.investingEntitiesIds.split(',').map(Number).some((id) => ids.includes(id))
          ) || [],
        funds:
          this.opportunitiesInvestments?.funds?.filter((fund) =>
            fund.investingEntitiesIds.split(',').map(Number).some((id) => ids.includes(id))
          ) || [],
      };
    }
    this.hasPendingInvestments = !!(this.filteredPendingInvestments.assets?.length || this.filteredPendingInvestments.funds?.length);
    this.hasOpportunitiesInvestments = !!(this.filteredOpportunitiesInvestments.assets?.length || this.filteredOpportunitiesInvestments.funds?.length);

    this.updateFilter.emit((investingEntities?.length && investingEntities.filter((entity) => entity.id)) || allItems);
  }

  get isViewingAsInvestor(): boolean {
    return this.impersonationService.isViewingAsInvestor;
  }

  get payappUrl() {
    return this.appSetting.payAppFrontEnvironmentAppBaseUrl;
  }

  getInvestmentsCountText(numberOfAssets: number, numberOfFunds: number, numberOfOpportunities: number) {
    if (isNullOrUndefined(numberOfAssets) && isNullOrUndefined(numberOfFunds) && isNullOrUndefined(numberOfOpportunities)) {
      return '';
    }

    if (numberOfAssets === 0 && numberOfFunds === 0 && numberOfOpportunities === 0) {
      return 'No Investments found';
    }

    const assetPlaceholder = numberOfAssets > 1 ? 'Assets' : 'Asset';
    const fundPlaceholder = numberOfFunds > 1 ? 'Funds' : 'Fund';
    const opportunitiesPlaceholder = numberOfOpportunities > 1 ? 'Opportunities' : 'Opportunity';
    const textParts = [...(numberOfAssets == 0 ? [] : [`${numberOfAssets} ${assetPlaceholder}`]),
    ...(numberOfFunds == 0 ? [] : [`${numberOfFunds} ${fundPlaceholder}`]),
    ...(numberOfOpportunities == 0 ? [] : [`${numberOfOpportunities} ${opportunitiesPlaceholder}`])];

    return textParts.join(' | ');
  }


  private generateForm() {
    this.pageForm = this.fb.group({
      searchTerm: null,
    });
  }

  private errorFetchingDataEventHandler(_error) {
    this.isLoading$.next(false);
    this.isGeneralServerError$.next(true);
    return EMPTY;
  }

  private sendTelemetryEvent() {
    if (this.isViewingAsInvestor) {
      this.userService.accountDetails$
        .pipe(take(1))
        .subscribe((userDetails) => {
          this.telemetryService.create(
            {
              eventID: '3003',
              eventTitle: 'GP-ViewAsInvestor-Success',
              sourcePage: document.location.href,
              ctNumber: userDetails.clientIdentifier,
              investorEmail:
                this.impersonationService.impersonateAsDetails?.username || '',
            },
            AnalyticsServiceNameModel.Mixpanel
          );
        });
    } else {
      this.telemetryService.create(
        {
          eventID: '5',
          eventTitle: 'LP HOME',
          additional: {
            isMobileDevice: TerraUtils.isMobileDevice(),
          },
        },
        AnalyticsServiceNameModel.Insights
      );
    }
  }
}
